import {
  Body,
  Button,
  FlexRow,
  Headline,
  Title1,
  Title2,
  useChameleonTheme,
} from "@petsapp/chameleon";
import { useAnalytics } from "@petsapp/use-analytics";
import { useState } from "react";
import styled from "styled-components";
import breakpoint from "styled-components-breakpoint";
import { useTranslation } from "../../../utils/i18n";
import { EVENTS } from "../events";
import { EntitlementsDialog } from "./entitlements-dialog";
import { WellnessPlan } from "../types";

const PlanContainer = styled.div`
  background-color: white;
  border-radius: 0px;
  padding: 2.5rem;
  margin-bottom: 2rem;

  @media (min-width: 400px) {
    border-radius: 12px;
  }
  ${breakpoint("tablet")`
    border-radius: 12px;
  `}
`;

const NoPlanContainer = styled.div`
  padding: 24px;
  border-radius: 0px;

  @media (min-width: 400px) {
    border-radius: 12px;
  }
`;

export const BasePlanView: React.FC<{
  name: string;
  description?: string;
  price: string;
  duration?: string;
  titleTextColor?: string;
  smallPrice?: boolean;
  pricePrefix?: string;
}> = ({
  name,
  smallPrice,
  description,
  price,
  duration,
  children,
  titleTextColor,
  pricePrefix,
}) => {
  const { t } = useTranslation();
  // @ts-ignore
  const { chameleon } = useChameleonTheme();

  const PriceComponent = smallPrice ? Title2 : Title1;

  return (
    <div>
      <FlexRow style={{ justifyContent: "space-between" }}>
        <div>
          <PriceComponent marginBottomSize={description ? 1 : 3}>
            {name}
          </PriceComponent>
          {description && <Body marginBottomSize={0}>{description}</Body>}
        </div>
        <div>
          <FlexRow
            style={{
              gap: "0.5rem",
              alignItems: "baseline",
              marginLeft: "8px",
            }}
          >
            {pricePrefix ? (
              <Headline textColor={chameleon.color.Grey} marginBottomSize={0}>
                {pricePrefix}
              </Headline>
            ) : null}
            <PriceComponent
              marginBottomSize={0}
              textColor={titleTextColor ?? chameleon.color.Primary}
            >
              {price}
            </PriceComponent>
          </FlexRow>
          {duration && (
            <Headline
              textColor={chameleon.color.Grey}
              style={{ textAlign: "right" }}
              marginBottomSize={1}
            >
              {t("per month")}
            </Headline>
          )}
        </div>
      </FlexRow>
      {children && (
        <FlexRow style={{ gap: "1rem", marginTop: "24px" }}>{children}</FlexRow>
      )}
    </div>
  );
};

export const NoPlan: React.FC<{ onChoosePlan(): void; loading: boolean }> = ({
  onChoosePlan,
  loading,
}) => {
  const { t } = useTranslation();
  // @ts-ignore
  const { chameleon } = useChameleonTheme();

  return (
    <NoPlanContainer
      style={{
        border: `2px solid ${chameleon.color.LightGrey}`,
      }}
    >
      <BasePlanView
        description={t("For any pet · Unlimited access to PetsApp")}
        name={t("No plan")}
        price={t("Free")}
        smallPrice={true}
        titleTextColor={chameleon.color.DarkGrey}
      >
        <Button
          state={loading ? "loading" : "active"}
          variant="secondary"
          onClick={() => {
            onChoosePlan();
          }}
        >
          {t("Continue without a plan")}
        </Button>
      </BasePlanView>
    </NoPlanContainer>
  );
};

export const usePlanPrice = (currencyCode: string) => {
  const { i18n } = useTranslation();
  const currency = Intl.NumberFormat(i18n.language, {
    style: "currency",
    currency: currencyCode,
  });

  return {
    format: (price: number) => currency.format(price / 100),
  };
};

export const Plan: React.FC<{
  plan: WellnessPlan;
  onChoosePlan(plan: WellnessPlan): void;
}> = ({ plan, onChoosePlan }) => {
  const { name, price, description, entitlements } = plan;
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { t } = useTranslation();
  const { format } = usePlanPrice(plan.currency);
  const { wrap } = useAnalytics();

  // @ts-ignore
  const formattedPrice = format(price);
  return (
    <>
      <PlanContainer>
        <BasePlanView
          name={name}
          price={formattedPrice}
          description={description}
        >
          <Button
            variant="primary"
            onClick={() => {
              onChoosePlan(plan);
            }}
          >
            {t("Choose plan")}
          </Button>
          <Button
            variant="secondary"
            onClick={wrap(() => setShowDialog(true), EVENTS.viewEntitlements, {
              name,
              price,
            })}
          >
            {t("View entitlements")}
          </Button>
        </BasePlanView>
      </PlanContainer>
      <EntitlementsDialog
        heading={`${t("For 12 months from the day you join you'll get")}:`}
        formattedPrice={formattedPrice}
        name={name}
        description={description}
        entitlements={entitlements}
        priceRange={t("per month")}
        showDialog={showDialog}
        onCancelDialog={() => setShowDialog(false)}
      />
    </>
  );
};
